/*
 | Runtime dos componentes de UI — escopo global, sem import/export.
 |
 | Fonte compartilhada pelas duas áreas: entra como primeiro arquivo tanto do
 | bundle do cms quanto do bundle do site. Compartilhar o fonte não mistura as
 | áreas — cada bundle sai com a sua própria cópia, e nenhuma página carrega o
 | JS da outra.
 |
 | Os componentes concatenados depois chamam registerComponent(), que o build
 | injeta em volta do corpo de cada _<nome>.js.
 |
 | O contrato com o Blade é o atributo data-component-name="<categoria>/<nome>".
 */

/* eslint no-unused-vars: 0 */

var Component = (function () {
    var registry = [];
    var listenersInstalled = false;

    /**
     * Inicializa os elementos que casam com o seletor, uma única vez cada.
     *
     * @param {string} selector
     * @param {Function} init
     * @param {Document|Element} root
     * @param {string} [name] Nome do componente, só para a mensagem de erro.
     */
    function createComponent(selector, init, root, name) {
        root = root || document;

        var components = Array.prototype.slice.call(root.querySelectorAll(selector));

        // querySelectorAll não inclui o próprio root — cobre o caso comum em
        // AJAX/Livewire onde o nó inserido É o componente.
        if (root instanceof Element && root.matches(selector)) {
            components.unshift(root);
        }

        components.forEach(function (component) {
            if (component.dataset.initialized === 'true') {
                return;
            }

            component.dataset.initialized = 'true';

            /*
             | Erro num componente não pode derrubar o boot dos outros.
             |
             | Sem isso, uma exceção sobe pelo forEach e todo componente
             | registrado depois deste deixa de inicializar — header, footer e
             | cookiebar incluídos, porque a ordem é a do concat. O componente
             | quebrado fica quebrado, e só ele, com o nome no console.
             */
            try {
                init(component);
            } catch (error) {
                console.error('Componente "' + (name || selector) + '" falhou ao inicializar:', error, component);
            }
        });
    }

    /**
     * @param {Element} component
     * @param {string} selector
     * @param {Function} callback
     */
    function withElement(component, selector, callback) {
        var element = component.querySelector(selector);

        if (! element) {
            return;
        }

        callback(element, component);
    }

    function bootAll(root) {
        root = root || document;

        registry.forEach(function (entry) {
            createComponent(entry.selector, entry.init, root, entry.name);
        });
    }

    function installListeners() {
        if (listenersInstalled) {
            return;
        }

        listenersInstalled = true;

        var onReady = function () {
            bootAll(document);
        };

        // Espera o DOMContentLoaded também em 'interactive': os bundles sob
        // demanda (charts, cropper) ainda não rodaram nesse ponto, e um boot
        // antecipado inicializaria o componente sem a lib global dele.
        if (document.readyState === 'complete') {
            queueMicrotask(onReady);
        } else {
            document.addEventListener('DOMContentLoaded', onReady, { once: true });
        }

        document.addEventListener('component:rescan', /** @param {CustomEvent<{ root?: Document|Element }>} event */ function (event) {
            bootAll((event && event.detail && event.detail.root) || document);
        });
    }

    /**
     * @param {string} name Identificador `categoria/nome`, igual ao data-component-name.
     * @param {Function} init
     */
    function registerComponent(name, init) {
        registry.push({ name: name, selector: '[data-component-name="' + name + '"]', init: init });
        installListeners();
    }

    /**
     * Reinicializa componentes dentro de um root (DOM injetado por AJAX/Livewire).
     *
     * @param {Document|Element} root
     */
    function rescanComponents(root) {
        bootAll(root || document);
    }

    return {
        define: registerComponent,
        register: registerComponent,
        rescan: rescanComponents,
        create: createComponent,
        withElement: withElement,
    };
})();

var registerComponent = Component.register;
var rescanComponents = Component.rescan;
var createComponent = Component.create;
var withElement = Component.withElement;

window.Component = Component;
window.registerComponent = registerComponent;
window.rescanComponents = rescanComponents;
window.createComponent = createComponent;
window.withElement = withElement;
